
import React, { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { Music, Headphones, BarChart3, Mail, ExternalLink, Filter, PlayCircle, CheckCircle2 } from 'lucide-react';

interface PlaylistMatch {
  id: string;
  name: string;
  curator: string;
  followers: number;
  matchScore: number;
  genre: string;
  status: 'not-pitched' | 'pitched' | 'accepted';
  fee: boolean;
}

const MOCK_PLAYLISTS: PlaylistMatch[] = [
  {
    id: '1',
    name: "Indie Discoveries",
    curator: "Spotify Editorial",
    followers: 287000,
    matchScore: 94,
    genre: "Indie Pop",
    status: 'not-pitched',
    fee: false
  },
  {
    id: '2',
    name: "Fresh Finds: Indie",
    curator: "Spotify Editorial",
    followers: 154000,
    matchScore: 88,
    genre: "Indie",
    status: 'not-pitched',
    fee: false
  },
  {
    id: '3',
    name: "Indie Morning Coffee",
    curator: "PlaylistHunter",
    followers: 46000,
    matchScore: 82,
    genre: "Indie Folk",
    status: 'pitched',
    fee: false
  },
  {
    id: '4',
    name: "Tomorrow's Hits",
    curator: "New Music Weekly",
    followers: 28000,
    matchScore: 78,
    genre: "Indie Pop/Rock",
    status: 'not-pitched',
    fee: true
  },
  {
    id: '5',
    name: "Bedroom Pop",
    curator: "Indie Shuffle",
    followers: 22000,
    matchScore: 76,
    genre: "Bedroom Pop",
    status: 'not-pitched',
    fee: false
  },
  {
    id: '6',
    name: "Acoustic Afternoons",
    curator: "ChillMusician",
    followers: 18500,
    matchScore: 72,
    genre: "Acoustic/Folk",
    status: 'accepted',
    fee: false
  },
];

const PlaylistPitching: React.FC = () => {
  const { toast } = useToast();
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [playlists, setPlaylists] = useState<PlaylistMatch[]>(MOCK_PLAYLISTS);
  const [filter, setFilter] = useState('all');
  
  const filteredPlaylists = filter === 'all' 
    ? playlists 
    : filter === 'pitched' 
      ? playlists.filter(p => p.status === 'pitched' || p.status === 'accepted')
      : playlists.filter(p => p.status === filter);

  const handleAnalyzeTrack = () => {
    setIsAnalyzing(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsAnalyzing(false);
      
      toast({
        title: "Track analyzed!",
        description: "We've found 28 playlist matches for your track."
      });
    }, 1500);
  };
  
  const handlePitch = (playlistId: string) => {
    setPlaylists(prev => 
      prev.map(p => 
        p.id === playlistId 
          ? {...p, status: 'pitched'} 
          : p
      )
    );
    
    toast({
      title: "Pitch sent!",
      description: "Your track has been pitched to the curator."
    });
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-2xl font-bold mb-1">Playlist Pitching</h2>
          <p className="text-muted-foreground">Find and pitch to playlists that match your sound</p>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="bg-background/70">
            <CardHeader className="pb-2">
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>AI Sound Matching</CardTitle>
                  <CardDescription>Upload your track to find matching playlists</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="flex items-center gap-1"
                  >
                    <Filter className="h-4 w-4" />
                    <span>Filter</span>
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-6 p-5 border border-dashed rounded-md">
                <div className="flex flex-col items-center text-center">
                  <Music className="h-10 w-10 text-muted-foreground mb-3" />
                  <h3 className="font-medium mb-1">Upload your track for analysis</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Our AI will analyze your music and find matching playlists
                  </p>
                  <div className="flex gap-2">
                    <Button>Upload Track</Button>
                    <Button variant="outline">Spotify Link</Button>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2 mb-4">
                <div className="flex justify-between items-center">
                  <h3 className="font-medium">Playlist Matches</h3>
                  <div className="flex gap-1">
                    <Badge 
                      variant="outline" 
                      className={`cursor-pointer ${filter === 'all' ? 'bg-muted' : ''}`}
                      onClick={() => setFilter('all')}
                    >
                      All
                    </Badge>
                    <Badge 
                      variant="outline" 
                      className={`cursor-pointer ${filter === 'pitched' ? 'bg-muted' : ''}`}
                      onClick={() => setFilter('pitched')}
                    >
                      Pitched
                    </Badge>
                    <Badge 
                      variant="outline" 
                      className={`cursor-pointer ${filter === 'accepted' ? 'bg-muted' : ''}`}
                      onClick={() => setFilter('accepted')}
                    >
                      Accepted
                    </Badge>
                  </div>
                </div>
              </div>
              
              <ScrollArea className="h-[320px] pr-4">
                <div className="space-y-3">
                  {filteredPlaylists.map((playlist) => (
                    <Card key={playlist.id}>
                      <div className="p-4 flex justify-between">
                        <div className="flex items-start gap-3">
                          <div className="bg-muted/50 h-12 w-12 rounded flex items-center justify-center">
                            <Headphones className="h-6 w-6 text-muted-foreground" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium">{playlist.name}</h3>
                              {playlist.status === 'pitched' && (
                                <Badge variant="outline" className="bg-blue-500/10 text-blue-500 border-blue-500/30">Pitched</Badge>
                              )}
                              {playlist.status === 'accepted' && (
                                <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/30">Accepted</Badge>
                              )}
                              {playlist.fee && (
                                <Badge variant="outline" className="bg-amber-500/10 text-amber-500 border-amber-500/30">Paid</Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{playlist.curator} • {playlist.followers.toLocaleString()} followers</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="secondary" className="text-xs">{playlist.genre}</Badge>
                              <div className="flex items-center gap-1 text-xs">
                                <BarChart3 className="h-3 w-3 text-green-500" />
                                <span className="text-green-500 font-medium">{playlist.matchScore}% match</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          {playlist.status === 'not-pitched' ? (
                            <Button 
                              size="sm" 
                              onClick={() => handlePitch(playlist.id)}
                              className="whitespace-nowrap"
                            >
                              <Mail className="h-3.5 w-3.5 mr-1" />
                              Pitch Track
                            </Button>
                          ) : playlist.status === 'pitched' ? (
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="whitespace-nowrap"
                              disabled
                            >
                              <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                              Pitched
                            </Button>
                          ) : (
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="whitespace-nowrap text-green-500 border-green-500/30"
                              disabled
                            >
                              <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                              Accepted
                            </Button>
                          )}
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="whitespace-nowrap"
                          >
                            <ExternalLink className="h-3.5 w-3.5 mr-1" />
                            View
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
            <CardFooter className="border-t bg-muted/20 justify-center py-2">
              <Button variant="link">View all 28 matches</Button>
            </CardFooter>
          </Card>
        </div>
        
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Track Analysis</CardTitle>
              <CardDescription>AI assessment of your track</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-muted/30 rounded-lg">
                <h3 className="text-sm font-medium mb-2">Currently Analyzing</h3>
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-muted h-12 w-12 rounded flex items-center justify-center">
                    <PlayCircle className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <div>
                    <h4 className="font-medium">Summer Dreams</h4>
                    <p className="text-xs text-muted-foreground">Uploaded 2 days ago</p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span>Genre: Indie Pop / Dream Pop</span>
                      <span className="text-green-500">98% confidence</span>
                    </div>
                    <div className="w-full bg-muted/50 rounded-full h-1.5">
                      <div className="bg-green-500 h-1.5 rounded-full" style={{ width: '98%' }}></div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span>Mood: Dreamy, Uplifting</span>
                      <span className="text-green-500">92% confidence</span>
                    </div>
                    <div className="w-full bg-muted/50 rounded-full h-1.5">
                      <div className="bg-green-500 h-1.5 rounded-full" style={{ width: '92%' }}></div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span>Similar Artists: CHVRCHES, Beach House</span>
                      <span className="text-green-500">88% confidence</span>
                    </div>
                    <div className="w-full bg-muted/50 rounded-full h-1.5">
                      <div className="bg-green-500 h-1.5 rounded-full" style={{ width: '88%' }}></div>
                    </div>
                  </div>
                </div>
              </div>
              
              <Separator />
              
              <div>
                <h3 className="text-sm font-medium mb-2">Your Pitch Stats</h3>
                <div className="grid grid-cols-2 gap-2">
                  <div className="p-3 bg-muted/30 rounded text-center">
                    <h4 className="text-2xl font-bold text-green-500">3</h4>
                    <p className="text-xs text-muted-foreground">Accepted</p>
                  </div>
                  <div className="p-3 bg-muted/30 rounded text-center">
                    <h4 className="text-2xl font-bold text-blue-500">12</h4>
                    <p className="text-xs text-muted-foreground">Pending</p>
                  </div>
                  <div className="p-3 bg-muted/30 rounded text-center">
                    <h4 className="text-2xl font-bold">25%</h4>
                    <p className="text-xs text-muted-foreground">Success Rate</p>
                  </div>
                  <div className="p-3 bg-muted/30 rounded text-center">
                    <h4 className="text-2xl font-bold">48h</h4>
                    <p className="text-xs text-muted-foreground">Avg. Response</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Blog & Influencer Pitching</CardTitle>
              <CardDescription>Find blogs and influencers for your music</CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full mb-4">Find Blogs & Influencers</Button>
              <div className="p-4 bg-muted/30 rounded-lg text-center">
                <p className="text-sm text-muted-foreground">
                  Analyze your track to discover music blogs and influencers that match your style
                </p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>AI Pitch Writer</CardTitle>
              <CardDescription>Generate personalized pitch emails</CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" className="w-full">
                Generate Pitch Email
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PlaylistPitching;
